<template>
  <div class="card">
    <h5 class="card-header">Profile</h5>
    <div class="card-body">
      <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
        {{ successMessage }}
        <button type="button" class="close" @click="successMessage = null" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ErrorSummary :error="error" />
      <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="form-group">
          <label for="legalName">Legal Name</label>
          <input
            v-model="legalName"
            type="text"
            class="form-control"
            id="legalName"
            placeholder="Legal Name"
            required
            :disabled="loadingMessage"
            @keyup.enter="submit"
          />
          <div class="invalid-feedback">
            Please provide a legal name.
          </div>
        </div>

        <div class="form-group">
          <label for="displayName">Display Name</label>
          <input
            v-model="displayName"
            type="text"
            class="form-control"
            id="displayName"
            placeholder="Display Name"
            required
            :disabled="loadingMessage"
            @keyup.enter="submit"
          />
          <div class="invalid-feedback">
            Please provide a display name.
          </div>
        </div>

        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="collectDemographicData" :disabled="loadingMessage" v-model="collectDemographicData" />
            <label class="custom-control-label" for="collectDemographicData">Collect Demographic Data</label>
          </div>
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input type="checkbox" class="custom-control-input" id="extendedTimeDefault" :disabled="loadingMessage" v-model="extendedTimeDefault" />
            <label class="custom-control-label" for="extendedTimeDefault">Interview Extended Time Default</label>
          </div>
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input
              type="checkbox"
              class="custom-control-input"
              id="sendInterviewFinishedNotificationEmail"
              :disabled="loadingMessage"
              v-model="sendInterviewFinishedNotificationEmail"
            />
            <label class="custom-control-label" for="sendInterviewFinishedNotificationEmail">Send Interview Finished Notification Email</label>
          </div>
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input
              type="checkbox"
              class="custom-control-input"
              id="showInvestigationRequestForContact"
              :disabled="loadingMessage"
              v-model="showInvestigationRequestForContact"
            />
            <label class="custom-control-label" for="showInvestigationRequestForContact">
              Show Request for Contact prompt at end of Investigation interview
            </label>
          </div>
        </div>
        <div class="form-group">
          <div class="custom-control custom-switch">
            <input
              type="checkbox"
              class="custom-control-input"
              id="showInvestigationTipReportScreen"
              :disabled="loadingMessage"
              v-model="showInvestigationTipReportScreen"
            />
            <label class="custom-control-label" for="showInvestigationTipReportScreen">
              Show Tip Report Screen at end of Investigation interview
            </label>
          </div>
        </div>
        <div class="form-group" v-if="showInvestigationTipReportScreen">
          <label for="tipReportScreenUrl">Tip Report Screen URL</label>
          <input
            v-model="tipReportScreenUrl"
            type="url"
            class="form-control"
            id="tipReportScreenUrl"
            placeholder="https://example.com/"
            required
            :disabled="loadingMessage"
            @keyup.enter="submit"
          />
          <small id="tipReportScreenUrl" class="form-text text-muted">
            Customize your organization's messaging on the
            <router-link :to="internationalization_href" v-slot="{ href, navigate }">
              <a :href="href" @click="navigate">Internationalization</a>
            </router-link>
            page.
          </small>
          <div class="invalid-feedback">
            Please provide a valid URL.
          </div>
        </div>
        <div class="form-group">
          <label for="displayName">Number of Days for Interview Invite Expiration</label>
          <select
            v-model.number="interviewInviteExpiration"
            :disabled="loadingMessage"
            id="interviewInviteExpiration"
            class="form-control"
            style="width: 70px;"
          >
            <option>4</option>
            <option>5</option>
            <option>6</option>
            <option>7</option>
            <option>8</option>
            <option>9</option>
            <option>10</option>
            <option>11</option>
            <option>12</option>
            <option>13</option>
            <option>14</option>
          </select>
        </div>
      </form>
    </div>
    <div class="card-footer">
      <button type="button" class="btn btn-primary btn-block" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
        <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        {{ loadingMessage ? loadingMessage : "Save" }}
      </button>
    </div>
  </div>
</template>

<script>
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      successMessage: null,
      error: null,

      legalName: null,
      displayName: null,
      collectDemographicData: false,
      extendedTimeDefault: false,
      sendInterviewFinishedNotificationEmail: false,
      showInvestigationRequestForContact: false,
      showInvestigationTipReportScreen: false,
      tipReportScreenUrl: null,
      interviewInviteExpiration: 8,
    };
  },
  computed: {
    internationalization_href() {
      return `/organizations/${this.organizationId}/internationalization`;
    },
  },
  created() {
    this.load();
  },
  watch: {
    organizationId() {
      this.load();
    },
  },
  methods: {
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}`)
        .then(res => {
          this.legalName = res.data.legalName;
          this.displayName = res.data.displayName;
          this.collectDemographicData = res.data.collectDemographicData;
          this.extendedTimeDefault = res.data.extendedTimeDefault;
          this.sendInterviewFinishedNotificationEmail = res.data.sendInterviewFinishedNotificationEmail;
          this.showInvestigationRequestForContact = res.data.showInvestigationRequestForContact;
          this.showInvestigationTipReportScreen = res.data.showInvestigationTipReportScreen;
          this.tipReportScreenUrl = res.data.tipReportScreenUrl;
          this.interviewInviteExpiration = res.data.interviewInviteExpiration;
        })
        .catch(() => this.$router.push("/404"))
        .finally(() => (this.loadingMessage = null));
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;
      this.successMessage = null;
      this.error = null;
    },
    submit() {
      const payload = {
        legalName: this.legalName,
        displayName: this.displayName,
        collectDemographicData: this.collectDemographicData,
        extendedTimeDefault: this.extendedTimeDefault,
        sendInterviewFinishedNotificationEmail: this.sendInterviewFinishedNotificationEmail,
        showInvestigationRequestForContact: this.showInvestigationRequestForContact,
        showInvestigationTipReportScreen: this.showInvestigationTipReportScreen,
        tipReportScreenUrl: this.tipReportScreenUrl,
        interviewInviteExpiration: this.interviewInviteExpiration,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .put(`/v1/organizations/${this.organizationId}`, payload)
        .then(() => (this.successMessage = "Save successful!"))
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
  },
};
</script>
